/**
 * My account — `/account`.
 *
 * The two things a signed-in user controls about themselves: the second factor
 * on their sign-in, and what they are willing to be told about.
 *
 * Enrolment is a three-step flow the screen refuses to soften.
 *
 * The provisioning secret is shown ONCE, in the response that mints it, and
 * expires in fifteen minutes. There is no way to fetch it again — so the
 * screen shows the countdown rather than pretending the code can be revisited.
 *
 * Confirming revokes every OTHER session and returns exactly ten recovery
 * codes that are never shown again. The screen says so before the button, not
 * after, and keeps the codes on screen until they are explicitly dismissed.
 *
 * Notification preferences are opt-outs: absence means enabled, and a template
 * rule outranks a category rule. Saving replaces the whole set, so removing a
 * row is how an opt-out is undone — which is why the screen lists what is
 * muted rather than pretending to list everything that could arrive.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, LogOut, Plus, ShieldCheck, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { formatDateTime } from "@/lib/format";
import { revokeAllSessions } from "@/lib/api/session";
import { useSession } from "@/lib/session/use-session";

import {
  NOTIFICATION_CHANNELS,
  confirmTOTPEnrollment,
  getNotificationPreferences,
  replaceNotificationPreferences,
  startTOTPEnrollment,
  type NotificationChannel,
  type NotificationPreference,
  type TOTPEnrollment,
} from "../account-api";

const PREFERENCES_KEY = ["platform/notification-preferences"];

export function AccountPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user, signOut } = useSession();

  const [password, setPassword] = useState("");
  const [enrollment, setEnrollment] = useState<TOTPEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState<NotificationPreference[] | null>(null);
  const [prefError, setPrefError] = useState<string | null>(null);
  const [prefSaved, setPrefSaved] = useState(false);
  const [newRule, setNewRule] = useState<{
    scope_type: "category" | "template";
    scope_value: string;
    channel: NotificationChannel;
  }>({ scope_type: "category", scope_value: "", channel: "email" });

  const preferences = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async ({ signal }) => [...(await getNotificationPreferences(signal)).items],
  });

  const rows = useMemo(() => draft ?? [...(preferences.data ?? [])], [draft, preferences.data]);

  const alreadyEnabled = user?.mfa_level === "totp";

  const stamp = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const start = () => {
    setBusy(true);
    setError(null);
    setRecoveryCodes(null);
    void startTOTPEnrollment(password)
      .then((started) => {
        setEnrollment(started);
        setPassword("");
      })
      .catch(() =>
        setError(
          t(
            "platform.account.startError",
            "Enrolment did not start. Check the password — and note that too many attempts are rate limited.",
          ),
        ),
      )
      .finally(() => setBusy(false));
  };

  const confirm = () => {
    setBusy(true);
    setError(null);
    void confirmTOTPEnrollment(code)
      .then(async (result) => {
        setRecoveryCodes(result.recovery_codes);
        setEnrollment(null);
        setCode("");
        // Every other session was just revoked and this one was re-issued at
        // TOTP level, so the cached session is stale.
        await queryClient.invalidateQueries();
      })
      .catch(() =>
        setError(
          t(
            "platform.account.confirmError",
            "That code was not accepted. It may have expired — a code is only good for its own moment.",
          ),
        ),
      )
      .finally(() => setBusy(false));
  };

  const savePreferences = () => {
    setPrefError(null);
    setPrefSaved(false);
    void replaceNotificationPreferences(rows)
      .then(async (result) => {
        setDraft([...result.items]);
        setPrefSaved(true);
        await queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY });
      })
      .catch(() =>
        setPrefError(t("platform.account.preferencesError", "The preferences could not be saved.")),
      );
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("platform.account.title", "My account")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {user === null
            ? null
            : t("platform.account.signedInAs", "Signed in as {{name}} ({{code}})", {
                name: user.display_name,
                code: user.code,
              })}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("platform.account.twoFactorTitle", "Two-factor authentication")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error === null ? null : (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          {recoveryCodes !== null ? (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm">
                <ShieldCheck className="size-4 text-success" aria-hidden="true" />
                {t(
                  "platform.account.enabled",
                  "Two-factor authentication is on. Every other session was signed out.",
                )}
              </p>
              <p className="text-sm text-destructive">
                {t(
                  "platform.account.saveCodes",
                  "Save these ten recovery codes now. They are not shown again, and each works once.",
                )}
              </p>
              <ul className="grid gap-1 rounded-md border p-3 font-mono text-sm sm:grid-cols-2">
                {recoveryCodes.map((recovery) => (
                  <li key={recovery} dir="ltr">
                    {recovery}
                  </li>
                ))}
              </ul>
              <Button type="button" variant="outline" onClick={() => setRecoveryCodes(null)}>
                {t("platform.account.savedCodes", "I have saved them")}
              </Button>
            </div>
          ) : alreadyEnabled && enrollment === null ? (
            <p className="flex items-center gap-2 text-sm">
              <ShieldCheck className="size-4 text-success" aria-hidden="true" />
              {t("platform.account.alreadyOn", "Two-factor authentication is already on.")}
            </p>
          ) : enrollment === null ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t(
                  "platform.account.startHint",
                  "Confirm your password to start. You will see the setup key once, and confirming signs out every other session.",
                )}
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="totp-password">
                    {t("platform.account.password", "Your password")}
                  </Label>
                  <Input
                    id="totp-password"
                    type="password"
                    className="w-64"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </div>
                <Button type="button" disabled={busy || password === ""} onClick={start}>
                  <KeyRound className="size-4" aria-hidden="true" />
                  {t("platform.account.start", "Start enrolment")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t(
                  "platform.account.secretHint",
                  "Add this key to your authenticator app, then enter the six digits it shows. The key is not shown again.",
                )}
              </p>
              <p className="rounded-md border p-3 font-mono text-lg tracking-widest" dir="ltr">
                {enrollment.secret}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("platform.account.expiresAt", "This key stops working at {{time}}.", {
                  time: stamp(enrollment.expires_at),
                })}
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="totp-code">{t("platform.account.code", "Six-digit code")}</Label>
                  <Input
                    id="totp-code"
                    dir="ltr"
                    inputMode="numeric"
                    maxLength={6}
                    className="w-32 text-center font-mono text-lg tracking-widest"
                    value={code}
                    onChange={(event) =>
                      setCode(event.target.value.replace(/[^0-9]/g, "").slice(0, 6))
                    }
                  />
                </div>
                <Button type="button" disabled={busy || code.length !== 6} onClick={confirm}>
                  {t("platform.account.confirm", "Turn it on")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("platform.account.sessionsTitle", "Where you are signed in")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t(
              "platform.account.sessionsHint",
              "Signing out everywhere ends every session, including this one — use it when a device is lost rather than merely closed.",
            )}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              // The server clears this cookie too, so the only honest next step
              // is the sign-out path the app already takes.
              void revokeAllSessions().finally(() => void signOut());
            }}
          >
            <LogOut className="size-4" aria-hidden="true" />
            {t("platform.account.signOutEverywhere", "Sign out everywhere")}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("platform.account.notificationsTitle", "What you are told about")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              "platform.account.notificationsHint",
              "Everything reaches you unless it is listed here. A rule about one notification outranks a rule about its whole category.",
            )}
          </p>

          {prefError === null ? null : (
            <p role="alert" className="text-sm text-destructive">
              {prefError}
            </p>
          )}
          {!prefSaved ? null : (
            <p role="status" className="text-sm text-muted-foreground">
              {t("platform.account.preferencesSaved", "Saved.")}
            </p>
          )}

          {preferences.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("platform.account.nothingMuted", "Nothing is muted; everything reaches you.")}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {rows.map((rule, index) => (
                <li
                  key={`${rule.scope_type}-${rule.scope_value}-${rule.channel}`}
                  className="flex flex-wrap items-center gap-3 rounded-md border p-2 text-sm"
                >
                  <Badge variant="secondary">
                    {t(`platform.scopeType.${rule.scope_type}`, rule.scope_type)}
                  </Badge>
                  <span dir="ltr" className="font-mono text-xs">
                    {rule.scope_value}
                  </span>
                  <Badge variant="outline">
                    {t(`platform.channel.${rule.channel}`, rule.channel)}
                  </Badge>
                  <span className="text-muted-foreground">
                    {rule.enabled
                      ? t("platform.account.forcedOn", "Always send")
                      : t("platform.account.muted", "Muted")}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t("platform.account.removeRule", "Remove this rule")}
                    onClick={() => setDraft(rows.filter((_, at) => at !== index))}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="grid gap-2 sm:grid-cols-[10rem_1fr_9rem_auto]">
            <Select
              value={newRule.scope_type}
              onValueChange={(value) =>
                setNewRule({ ...newRule, scope_type: value as "category" | "template" })
              }
            >
              <SelectTrigger aria-label={t("platform.account.scopeType", "Applies to")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="category">
                  {t("platform.scopeType.category", "A whole category")}
                </SelectItem>
                <SelectItem value="template">
                  {t("platform.scopeType.template", "One notification")}
                </SelectItem>
              </SelectContent>
            </Select>
            <Input
              dir="ltr"
              className="font-mono"
              aria-label={t("platform.account.scopeValue", "Its code")}
              placeholder={
                newRule.scope_type === "category"
                  ? t("platform.account.categoryExample", "inventory")
                  : t("platform.account.templateExample", "stock_below_reorder_point")
              }
              value={newRule.scope_value}
              onChange={(event) =>
                setNewRule({
                  ...newRule,
                  scope_value: event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                })
              }
            />
            <Select
              value={newRule.channel}
              onValueChange={(value) =>
                setNewRule({ ...newRule, channel: value as NotificationChannel })
              }
            >
              <SelectTrigger aria-label={t("platform.account.channel", "Channel")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NOTIFICATION_CHANNELS.map((channel) => (
                  <SelectItem key={channel} value={channel}>
                    {t(`platform.channel.${channel}`, channel)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              disabled={!/^[a-z][a-z0-9_]{0,63}$/.test(newRule.scope_value)}
              onClick={() => {
                setDraft([
                  ...rows.filter(
                    (rule) =>
                      !(
                        rule.scope_type === newRule.scope_type &&
                        rule.scope_value === newRule.scope_value &&
                        rule.channel === newRule.channel
                      ),
                  ),
                  { ...newRule, enabled: false },
                ]);
                setNewRule({ ...newRule, scope_value: "" });
                setPrefSaved(false);
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              {t("platform.account.mute", "Mute it")}
            </Button>
          </div>

          <Button type="button" disabled={draft === null} onClick={savePreferences}>
            {t("actions.save", "Save")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
